<script>
	import { onMount } from "svelte";
	import PageSection from "../../components/PageSection.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	import { acceptedFileUploadList, yesNoOptions, yesNoUnsureOptions, projectSettings } from "../../config";
	import { pageNumber, model } from "../../stores";
	import { validator } from "../../validation";
	import Loan from "../../models/Loan";

	export let title = null;
	export let pageIndex = null;
	export let pageTitle = null;
	export let nextPage = null;
	export let previousPage = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;
	export let disabled = null;

	let required = true;
	let disableNextButton = false;
	let id = null;
	let prefix = "dz";
	let options = {};
	let liveIndex = 0;
	let liveLoan = null;
	let documentUploadError = "";

	if (!disabled) {
		$pageNumber = pageIndex;
		$model.LastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	onMount(() => {
		disableNextButton = $model.HomeLoanViaAHL === "No" ? true : false;
	});

	if ($model.Loans.length === 0) $model.Loans = [new Loan()];

	$: {
		liveLoan = $model.Loans[liveIndex];

		id = `${prefix}_${liveIndex}`;
		options = {
			url: `/api/file-upload?token=${$model.RegistrationToken}&id=${liveLoan.LoanGuid.substring(0, liveLoan.LoanGuid.indexOf("-"))}`,
			acceptedFiles: acceptedFileUploadList
		};
	}

	const handleHomeLoanViaAHL = (e) => {
		if (e.target.value == "No") {
			disableNextButton = true;
			alert(
				"Based on the information provided, you will not be eligible to register for this Class Action. Please contact us on 1800 325 172 or via email to aussiehomeloans@shine.com.au if you wish to discuss this further."
			);
		} else {
			disableNextButton = false;
		}
	};

	const validateDocuments = () => {
		let isValid = liveLoan.DocumentProvided === "Yes" && !liveLoan.Documents.length ? false : true;
		documentUploadError = isValid ? "" : "Please upload documents";
		return isValid;
	};

	const getDocumentUploadError = () => {
		let isValid = validateDocuments();
		return isValid ? [] : [document.getElementById("docUploadError")];
	};

	const addLoan = () => {
		let isValid = validateDocuments();
		if ($validator.validateAll(document.querySelector("form")).length > 0 || !isValid) return;
		$model.Loans = [...$model.Loans, new Loan()];
		liveIndex = $model.Loans.length - 1;
	};

	const removeLoan = (index) => {
		if (liveIndex === index) liveIndex = 0;
		else if (liveIndex > index) liveIndex -= 1;
		$model.Loans = [...$model.Loans.filter((_, i) => i !== index)];
	};

	const setLiveLoan = (index) => {
		liveIndex = index;
		$validator.clearAllVisualError(document.querySelector("form"));
	};

	$: if (liveLoan.LoanName) {
		liveLoan = $model.Loans[liveIndex];
	}
</script>

<PageSection>
	<h2>Claim Details</h2>

	{#if !disabled}
		<p>
			If you are unable to complete any part of this section of the form, we are here to assist you. Please
			contact us on {projectSettings.phoneNumber} or via email to
			<a href="mailto:{projectSettings.emailAddress}" target="_blank" rel="noreferrer noopener"
				>{projectSettings.emailAddress}</a
			>.
		</p>
	{/if}

	<FormRow>
		<FormColumn>
			<YesNoGroup
				{required}
				{disabled}
				label="Did you obtain a home loan through Aussie Home Loans?"
				name="homeLoanViaAHL"
				bind:value={$model.HomeLoanViaAHL}
				optionList={yesNoOptions}
				on:change={handleHomeLoanViaAHL}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	{#if $model.HomeLoanViaAHL === "Yes"}
		{#if disabled}
			<div class="container">
				<div class="row">
					<h3>Loan Details</h3>
					{#each $model.Loans as loan, i}
						<div class="form-group">
							<h4>{loan.LoanName}</h4>
							<RadioGroup
								{required}
								{disabled}
								label="At or around the time of obtaining your home loan through Aussie Home Loans, or at any time after obtaining that home loan, did you receive advice, or a recommendation, to acquire a Mortgage Protection Plan or My Protection Plan policy from your Aussie Home Loans broker?"
								name="includeMortgageProtectionPolicy_{i}"
								bind:value={loan.IncludeMortgageProtectionPolicy}
								optionList={yesNoUnsureOptions}
							></RadioGroup>

							{#if loan.IncludeMortgageProtectionPolicy === "Yes"}
								<RadioGroup
									{required}
									{disabled}
									label="Did you rely on that advice or recommendation to acquire a Mortgage Protection Plan or My Protection Plan policy?"
									name="replyOnAdvice_{i}"
									bind:value={loan.RelyOnAdvice}
									optionList={yesNoUnsureOptions}
								></RadioGroup>
							{/if}

							{#if loan.RelyOnAdvice == "Yes"}
								<br />
								<RadioGroup
									{required}
									{disabled}
									label="Did the Aussie Home Loans broker tell you that the policy would help you get your home loan, or that it was required in order to get your home loan?"
									name="toldPolicyEssential_{i}"
									bind:value={loan.ToldPolicyEssential}
									optionList={yesNoUnsureOptions}
								></RadioGroup>
							{/if}

							{#if loan.ToldPolicyEssential == "Yes"}
								<br />
								<RadioGroup
									{required}
									{disabled}
									label="Did you already have an insurance policy (including inside your superannuation) that provided you with death, disability and/or income protection cover?"
									name="alreadyHadDeathDisabilityIncomeProtectionCover_{i}"
									bind:value={loan.alreadyHadDeathDisabilityIncomeProtectionCover}
									optionList={yesNoUnsureOptions}
								></RadioGroup>
							{/if}

							<br />
							<YesNoGroup
								{required}
								{disabled}
								label="Are you able to provide us with a copy of the document(s) you were provided by Aussie Home Loans in relation to this policy?"
								name="documentProvided_{i}"
								bind:value={loan.DocumentProvided}
								optionList={yesNoOptions}
							></YesNoGroup>

							<div class="form-row" style="padding-top:10px;">
								<div class="form-group col-md-12">
									<p>Loan Documents</p>
									{#if loan.Documents && loan.Documents.length}
										<table class="table table-sm">
											<tr>
												<th>Document Name</th>
											</tr>
											{#each loan.Documents as file}
												<tr>
													<td>{file.fileName}</td>
												</tr>
											{/each}
										</table>
									{:else}
										<p>No documents uploaded</p>
									{/if}
								</div>
							</div>
						</div>
					{/each}
				</div>
			</div>
		{:else}
			<FormRow>
				<div class="container">
					<div class="row panelWrapper">
						<div class="col-sm-3 listPanel">
							<h3>Home Loan</h3>

							{#each $model.Loans as loan, i (loan)}
								<div class="listPanelItem">
									<p class="listPanelItemName">{(loan.LoanName = `Loan ${i + 1}`)}</p>
									<span class="listPanelButtonWrapper">
										<button
											type="button"
											class="btn btn-outline-secondary btn-sm"
											style={`visibility: ${liveIndex === i ? "hidden" : "visible"}`}
											on:click|preventDefault={() => setLiveLoan(i)}>Edit</button
										>
										<button
											type="button"
											class="btn btn-outline-secondary btn-sm"
											style={`visibility: ${$model.Loans.length === 1 ? "hidden" : "visible"}`}
											on:click|preventDefault={() => removeLoan(i)}>Remove</button
										>
									</span>
								</div>
							{/each}

							{#if !disabled}
								<button
									class="btn btn-primary btn-sm"
									style="margin-top: 5px"
									title={`Add Loan`}
									on:click|preventDefault={() => addLoan()}>Add Home Loan</button
								>
							{/if}
						</div>
						<div class="col-sm-9">
							<h3>Loan Details</h3>
							<p>
								The following sections are required to be completed per loan, if you wish to submit
								multiple loans you may do so by selecting "Add Home Loan".
							</p>
							<RadioGroup
								{required}
								{disabled}
								label="At or around the time of obtaining your home loan through Aussie Home Loans, or at any time after obtaining that home loan, did you receive advice, or a recommendation, to acquire a Mortgage Protection Plan or My Protection Plan policy from your Aussie Home Loans broker?"
								name="includeMortgageProtectionPolicy"
								bind:value={liveLoan.IncludeMortgageProtectionPolicy}
								optionList={yesNoUnsureOptions}
							></RadioGroup>

							<br />
							<RadioGroup
								{required}
								{disabled}
								label="Did you rely on that advice or recommendation to acquire a Mortgage Protection Plan or My Protection Plan policy?"
								name="replyOnAdvice"
								bind:value={liveLoan.RelyOnAdvice}
								optionList={yesNoUnsureOptions}
							></RadioGroup>

							{#if liveLoan.RelyOnAdvice == "Yes"}
								<br />
								<RadioGroup
									{required}
									{disabled}
									label="Did the Aussie Home Loans broker tell you that the policy would help you get your home loan, or that it was required in order to get your home loan?"
									name="toldPolicyEssential"
									bind:value={liveLoan.ToldPolicyEssential}
									optionList={yesNoUnsureOptions}
								></RadioGroup>
							{/if}

							{#if liveLoan.ToldPolicyEssential == "Yes"}
								<br />
								<RadioGroup
									{required}
									{disabled}
									label="Did you already have an insurance policy (including inside your superannuation) that provided you with death, disability and/or income protection cover?"
									name="alreadyHadDeathDisabilityIncomeProtectionCover"
									bind:value={liveLoan.alreadyHadDeathDisabilityIncomeProtectionCover}
									optionList={yesNoUnsureOptions}
								></RadioGroup>
							{/if}

							<br />
							<YesNoGroup
								{required}
								{disabled}
								label="Are you able to provide us with a copy of the document(s) you were provided by Aussie Home Loans in relation to this policy?"
								name="documentProvided"
								bind:value={liveLoan.DocumentProvided}
								optionList={yesNoOptions}
							></YesNoGroup>

							{#if liveLoan.DocumentProvided === "Yes"}
								<div class="form-row">
									<div class="form-group col-md-12">
										{#key liveIndex}
											<DropZone
												{options}
												{id}
												{disabled}
												message="Drop files here or click to upload"
												bind:documentList={liveLoan.Documents}
											></DropZone>
										{/key}
									</div>
								</div>
								{#if documentUploadError}
									<div id="docUploadError" class="error-message">
										{documentUploadError}
									</div>
								{/if}
							{/if}
						</div>
					</div>
				</div>
			</FormRow>
		{/if}

		<FormRow>
			<FormColumn>
				<TextArea
					{disabled}
					required={false}
					label="Is there anything else you want to tell us?"
					id="notes"
					name="notes"
					bind:value={$model.Notes}
					autoExpand={true}
				/>
			</FormColumn>
		</FormRow>
	{/if}

	{#if !disabled}
		<div class="form-row">
			<div class="col-md-12 last">
				<NavigationButtons
					onValidate={getDocumentUploadError}
					{persistUrl}
					{previousPage}
					{nextPage}
					{disableNextButton}
					tealiumTag={true}
					pageName={title}
				></NavigationButtons>
			</div>
		</div>
	{/if}
</PageSection>

<style>
	.container {
		padding-top: 30px;
		padding-bottom: 30px;
	}

	.listPanel {
		padding-top: 5px;
	}

	.listPanelItem {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 2px;
	}

	.listPanelItemName {
		margin: 0;
		text-overflow: ellipsis;
		overflow: hidden;
		padding-right: 10px;
		white-space: nowrap;
	}

	.listPanelButtonWrapper {
		white-space: nowrap;
	}

	@media only screen and (max-width: 991px) {
		.panelWrapper {
			flex-direction: column;
		}
		.listPanel {
			padding-top: 0;
			max-width: unset;
			margin-bottom: 10px;
		}
	}
</style>
